import { useState } from "react";
import { ChevronDown, Download, RotateCcw, Settings } from "react-feather";
import { PanelTypeGenerationStatus } from "../../../../../responses/panelGeneratedModelDto";
import { useDeferredSort } from "../../../../common/useDeferredSort";
import { CategoryType } from "../../../typesManager/constants/panelTypeEnums";
import { compareAlphanumeric } from "../../../utils/sorting";
import { PanelTypeView, PanelView } from "./mapToViewable";
import PanelSection from "./panelSection";
import "../../../../../assets/typesManagerStyles.css";
import eventBus from "../../../eventBus/eventDispatcher";

interface PanelTypeSectionProps {
    panelType: PanelTypeView;
    selectedPanelId: string | null;
    categoryName: CategoryType;
}

const compareUserUniqueIds = (a: PanelView, b: PanelView) => {
    const aStr = a.userUniqueId.toString();
    const bStr = b.userUniqueId.toString();
    return compareAlphanumeric(aStr, bStr);
};

const PanelTypeSection = ({ panelType, selectedPanelId, categoryName }: PanelTypeSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const sortedPanels = useDeferredSort(
        panelType.panels,
        isExpanded,
        compareUserUniqueIds,
    );

    const toggleGroup = () => setIsExpanded(prevState => !prevState);

    const generateSinglePanel = (panelTypeId: string, isPanelModel: boolean) => {
        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.SingleGeneration.Requested",
            payload: { panelTypeId, isPanelModel },
        });
    };

    const downloadModel = (modelId: string) => {
        eventBus.dispatchEvent({ type: "Dextall.GeneratedModel.Download", payload: modelId });
    };

    const onButtonClick = () => {
        if (panelType.status === PanelTypeGenerationStatus.ModelGeneratedSuccessfully) {
            if (panelType.generatedModelId) {
                downloadModel(panelType.generatedModelId);
            }
        } else {
            generateSinglePanel(panelType.id, isPanelModel());
        }
    };

    const isPanelModel = (): boolean => {
        return categoryName === CategoryType.Opaque || categoryName === CategoryType.Window;
    };

    const onPanelTypeClick = () => {
        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.UI.PanelTypeSelected",
            payload: {
                panelTypeId: panelType.id,
                panelCategory: categoryName,
            },
        });
    };

    return (
        <div>
            <h5 className="tm-panel-type-title">
                <button
                    className={`tm-button tm-panel-type-header ${panelType.isSelected ? "tm-selected" : ""}`}
                    onClick={onPanelTypeClick}
                >
                    <span className="tm-panel-type-header-content">
                        <span className="tm-panel-type-name">{panelType.name}</span>
                        <span className="tm-panel-type-name-count">({panelType.panels.length})</span>
                    </span>
                </button>
                <span className="tm-status-wrapper">
                    {panelType.status === PanelTypeGenerationStatus.ModelGenerationFailed && (
                        <span className="tm-status-box tm-status-failed">Failed</span>
                    )}
                    {panelType.status === PanelTypeGenerationStatus.None && <span className="tm-status-box">None</span>}
                    {panelType.status === PanelTypeGenerationStatus.ModelGeneratedSuccessfully && (
                        <span className="tm-status-box tm-status-generated">Generated</span>
                    )}
                    {panelType.status === PanelTypeGenerationStatus.ModelGenerationInProgress && (
                        <span className="tm-status-box tm-status-in-progress">In Progress..</span>
                    )}
                    {panelType.status === null && <span>Info Loading..</span>}
                </span>
                <button onClick={onButtonClick} className="tm-status-icons">
                    {panelType.status === PanelTypeGenerationStatus.ModelGenerationFailed && (
                        <RotateCcw size={16} color="lightgray" />
                    )}
                    {panelType.status === PanelTypeGenerationStatus.None && <Settings size={16} color="#0796D8" />}
                    {panelType.status === PanelTypeGenerationStatus.ModelGeneratedSuccessfully && (
                        <Download size={16} color="#0796D8" />
                    )}
                    {panelType.status === PanelTypeGenerationStatus.ModelGenerationInProgress && (
                        <Download size={16} color="gray" className="tm-button-not-allowed" />
                    )}
                </button>
                <ChevronDown
                    size={16}
                    className="tm-chevron"
                    style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}
                    onClick={toggleGroup}
                />
            </h5>
            {isExpanded && (
                <div>
                    {sortedPanels.map(panel => (
                        <PanelSection
                            panel={panel}
                            selectedPanelId={selectedPanelId}
                            categoryName={categoryName}
                            key={panel.id}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default PanelTypeSection;